/**
 * Collection Management schema — the frontend-consumable contract.
 *
 * A COLLECTION is a structured content type: a name, a base path its entries
 * live under, a schema of fields those entries carry, a default template they
 * are drawn with, and a mapping saying which of those fields to show on a card.
 * News is one; a Blog would be another.
 *
 * An ENTRY is a Page — with a `collectionId` and a set of field values. It reuses
 * versions, publishing, locales, the editor and the audit trail wholesale, which
 * is the entire reason for modelling it that way: a press release is a page that
 * happens to have a date and a source, not a second kind of thing with its own
 * half-built lifecycle. Field values are versioned WITH the page and go live on
 * publish, so a card can never show a detail from an unpublished draft.
 *
 * What a Collection owns that a Template does not: the URL namespace. Templates
 * became presentation on 2026-09-06 and gave up their base paths; this is where
 * they went, because a URL prefix is a property of a group of CONTENT, not of a
 * way of drawing it.
 *
 * Pure, serializable data — no React, no DOM. Shared by @diality/cms (manage
 * screens + stub), @diality/api (persistence + server validation), @diality/ui
 * (the Collection List block) and the acceptance harness. As with template.ts
 * and site-settings.ts this module is the single source of truth for BOTH the
 * shapes and the create/update rules, so the CMS's inline feedback and the API's
 * refusals cannot disagree.
 *
 * Uniqueness (a base path claimed by at most one collection) is a 409 the
 * service resolves against the store; the pure rules here are the 400/422-class
 * checks only.
 */
import { validateBasePathValue } from './base-path';
/** An empty set of field values — what a new entry starts with. */
export const EMPTY_ENTRY_FIELDS = { scalars: {}, media: {} };
/** True for an entry that has no page of its own. */
export function isLinkEntry(entry) {
    return entry.kind === 'link';
}
// ── Field keys ────────────────────────────────────────────────────────────
/**
 * A field key: lowerCamelCase, letters and digits only.
 *
 * Narrow on purpose. A key is a DynamoDB attribute name, a query-string filter
 * token, a JSON property and a form control's id all at once — anything that
 * needs quoting or escaping in one of those places is a bug waiting for the
 * first field somebody names "publication date".
 */
const FIELD_KEY_PATTERN = /^[a-z][a-zA-Z0-9]*$/;
/**
 * Names an entry already has, which a field may not shadow.
 *
 * These are properties of the ENTRY, not of its schema — every entry has a
 * title and a url whatever fields it carries — and the entries endpoint sorts
 * and filters over both namespaces. A field called `title` would make
 * `sort=title` ambiguous, and the ambiguity would only surface as the wrong
 * order on somebody's news page.
 */
export const RESERVED_FIELD_KEYS = [
    'id',
    'url',
    'slug',
    'title',
    'publishedAt',
    'status',
    // Every entry has a kind; a field of the same name would shadow it.
    'kind',
];
/** Entry properties that are always sortable, whatever the schema says. */
export const INTRINSIC_SORT_FIELDS = ['publishedAt', 'title'];
export function isValidFieldKey(key) {
    return FIELD_KEY_PATTERN.test(key);
}
export function isReservedFieldKey(key) {
    return RESERVED_FIELD_KEYS.includes(key);
}
/** Every field key a schema declares, scalars then media then documents. */
export function fieldKeys(fields) {
    return [
        ...fields.scalarSlots.map((s) => s.key),
        ...fields.mediaSlots.map((s) => s.key),
        ...(fields.documentSlots ?? []).map((s) => s.key),
    ];
}
/** The scalar slot a key names, or undefined. */
export function scalarSlotFor(fields, key) {
    if (!key)
        return undefined;
    return fields.scalarSlots.find((s) => s.key === key);
}
/**
 * Validate a field schema: every key well-formed, unique across ALL three slot
 * kinds, and not one an entry already has.
 *
 * Uniqueness spans the kinds because the three collapse into one namespace the
 * moment an entry is written — a scalar and a media slot both called `image`
 * would be two fields with one attribute name.
 *
 * Returns the error naming the FIRST offender, or `null`.
 */
export function validateFieldSchema(fields) {
    const seen = new Set();
    for (const key of fieldKeys(fields)) {
        if (!isValidFieldKey(key)) {
            return {
                field: 'fields',
                code: 'invalid',
                message: `"${key}" is not a valid field key — use letters and digits, starting with a lowercase letter`,
            };
        }
        if (isReservedFieldKey(key)) {
            return {
                field: 'fields',
                code: 'reserved',
                message: `"${key}" is a name every entry already has`,
            };
        }
        if (seen.has(key)) {
            return {
                field: 'fields',
                code: 'duplicate',
                message: `"${key}" is used by more than one field`,
            };
        }
        seen.add(key);
    }
    return null;
}
/**
 * Validate a display mapping: every mapped key must exist, and the three roles
 * with a TYPE requirement must be filled by a field that can satisfy it.
 *
 * A date shown on a card has to sort and format, so it must be a `date` scalar;
 * a card that leaves the site needs a `url`; a picture needs a media slot.
 * Title, category, source and summary are shown verbatim and take any scalar.
 */
export function validateDisplay(display, fields) {
    const known = new Set(fieldKeys(fields));
    const named = [
        ['titleField', display.titleField],
        ['dateField', display.dateField],
        ['categoryField', display.categoryField],
        ['sourceField', display.sourceField],
        ['summaryField', display.summaryField],
        ['imageField', display.imageField],
        ['linkField', display.linkField],
    ];
    for (const [role, key] of named) {
        if (!key)
            continue;
        if (!known.has(key)) {
            return {
                field: 'display',
                code: 'invalid',
                message: `"${key}" is not one of this collection's fields`,
            };
        }
    }
    const scalarType = (key) => scalarSlotFor(fields, key)?.type;
    if (display.dateField && scalarType(display.dateField) !== 'date') {
        return {
            field: 'display',
            code: 'invalid',
            message: `"${display.dateField}" is not a date field`,
        };
    }
    if (display.linkField && scalarType(display.linkField) !== 'url') {
        return {
            field: 'display',
            code: 'invalid',
            message: `"${display.linkField}" is not a link field`,
        };
    }
    if (display.imageField &&
        !fields.mediaSlots.some((s) => s.key === display.imageField)) {
        return {
            field: 'display',
            code: 'invalid',
            message: `"${display.imageField}" is not a picture field`,
        };
    }
    return null;
}
/** Validate the default ordering: a declared field, or an intrinsic one. */
export function validateSortField(sortField, fields) {
    if (!sortField)
        return null; // absent means the default, applied on read
    if (INTRINSIC_SORT_FIELDS.includes(sortField)) {
        return null;
    }
    if (!fieldKeys(fields).includes(sortField)) {
        return {
            field: 'sortField',
            code: 'invalid',
            message: `"${sortField}" is not one of this collection's fields`,
        };
    }
    return null;
}
function asCollectionError(error) {
    return { field: 'basePath', code: error.code, message: error.message };
}
/**
 * Validate a create payload and return the normalized value or the field
 * errors. All-or-nothing, like templates and site settings.
 *
 * `sortField` defaults to `publishedAt` and the direction to newest-first,
 * because that is what every list of dated content wants and a collection
 * created without a thought about ordering should not come out shuffled.
 */
export function validateCollectionCreate(input) {
    const name = (input.name ?? '').trim();
    const basePath = (input.basePath ?? '').trim();
    const fields = {
        scalarSlots: input.fields?.scalarSlots ?? [],
        mediaSlots: input.fields?.mediaSlots ?? [],
        ...(input.fields?.documentSlots
            ? { documentSlots: input.fields.documentSlots }
            : {}),
    };
    const display = input.display ?? {};
    const sortField = (input.sortField ?? 'publishedAt').trim();
    const sortDirection = input.sortDirection === 'asc' ? 'asc' : 'desc';
    const errors = [];
    if (!name) {
        errors.push({ field: 'name', code: 'required', message: 'Name is required' });
    }
    const basePathError = validateBasePathValue(basePath);
    if (basePathError)
        errors.push(asCollectionError(basePathError));
    const schemaError = validateFieldSchema(fields);
    if (schemaError)
        errors.push(schemaError);
    // Only worth checking once the schema itself is sound — a display mapping
    // validated against a broken schema reports the same problem twice.
    if (!schemaError) {
        const displayError = validateDisplay(display, fields);
        if (displayError)
            errors.push(displayError);
        const sortError = validateSortField(sortField, fields);
        if (sortError)
            errors.push(sortError);
    }
    if (errors.length > 0)
        return { ok: false, errors };
    return {
        ok: true,
        value: {
            name,
            basePath,
            fields,
            defaultTemplateId: (input.defaultTemplateId ?? '').trim(),
            display,
            sortField,
            sortDirection,
        },
    };
}
/**
 * Keep only scalars the schema declares.
 *
 * Silently DROPS unknown keys rather than refusing the write. A field removed
 * from the schema leaves values behind on entries that already had it, and a
 * later edit of such an entry would otherwise be refused for carrying a value
 * the author never typed and cannot see.
 */
export function validateEntryScalars(scalars, fields) {
    const declared = new Set(fields.scalarSlots.map((s) => s.key));
    const kept = {};
    for (const [key, value] of Object.entries(scalars)) {
        if (declared.has(key))
            kept[key] = value;
    }
    return kept;
}
/**
 * Validate a link entry.
 *
 * Both answers are required and neither has a sensible default: a card with no
 * title says nothing, and one with no URL is a link to nowhere — which is the
 * whole of what a link entry is. The URL must be ABSOLUTE and http(s): a
 * relative one would mean "somewhere on this site", and a page entry is how you
 * say that.
 *
 * Scalars are pruned through {@link validateEntryScalars}, exactly as a page
 * entry's fields are, so an undeclared key cannot be smuggled in through either
 * door.
 */
export function validateLinkEntry(input, fields) {
    const errors = [];
    const title = (input.title ?? '').trim();
    const url = (input.url ?? '').trim();
    if (!title) {
        errors.push({
            field: 'title',
            code: 'required',
            message: 'A title is required',
        });
    }
    if (!url) {
        errors.push({
            field: 'url',
            code: 'required',
            message: 'A link is required',
        });
    }
    else if (!/^https?:\/\/\S+$/i.test(url)) {
        errors.push({
            field: 'url',
            code: 'invalid',
            message: 'Enter a full link, starting with http:// or https://',
        });
    }
    if (errors.length > 0)
        return { ok: false, errors };
    return {
        ok: true,
        value: {
            title,
            url,
            scalars: validateEntryScalars(input.scalars ?? {}, fields),
        },
    };
}
// ── Reading an entry ──────────────────────────────────────────────────────
/**
 * The card for one entry.
 *
 * `href` is the entry's `url` and `external` is its `kind` — one destination,
 * decided by what the entry IS. A page entry's card leads to its own page; a
 * link entry has no page, and its url IS the outbound one.
 *
 * This used to read the display's `linkField` and send a card off-site whenever
 * that field happened to be filled, so a collection held two behaviours with
 * nothing in its settings to show which entry did which. `linkField` now only
 * retains original-source metadata for compatibility (see CollectionDisplay).
 *
 * `image` is left to the caller to resolve: this module is React-free and knows
 * nothing about crop URLs, so it passes the ref's resolution through.
 */
export function entryCard(collection, entry, resolveImage) {
    const { display } = collection;
    const scalar = (key) => key ? (entry.fields.scalars[key] ?? '') || undefined : undefined;
    const imageRef = display.imageField
        ? entry.fields.media[display.imageField]
        : null;
    return {
        id: entry.id,
        title: scalar(display.titleField) ?? entry.title,
        href: entry.url,
        external: entry.kind === 'link',
        date: formatEntryDate(scalar(display.dateField)),
        category: scalar(display.categoryField ?? 'category'),
        source: scalar(display.sourceField ?? 'source'),
        summary: scalar(display.summaryField),
        image: imageRef && resolveImage ? resolveImage(imageRef) : null,
    };
}
/**
 * A date field as a reader should see it.
 *
 * A `date` field stores a calendar day (`2026-02-24`) because that is what
 * sorts and what a date input produces — but nobody reads a press release dated
 * "2026-02-24". Formatting here rather than in each card means the site, the
 * masthead and the CMS hub cannot disagree about how a date looks.
 *
 * Anything that is not a calendar day is passed through UNCHANGED. A collection
 * is free to map its date role onto a text field holding "Spring 2026", and
 * rewriting that would be inventing precision the author did not give.
 *
 * en-US, deliberately: it is the site's default locale and the form every date
 * on it has always taken. A localised site formats per locale, and that is a
 * decision for whoever adds the second locale, not a default to guess at now.
 */
export function formatEntryDate(value) {
    if (!value)
        return undefined;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match)
        return value;
    const [, year, month, day] = match;
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    if (Number.isNaN(date.getTime()))
        return value;
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
    });
}
/**
 * The masthead for one entry, for the article layout.
 *
 * Category and source use their explicit display mappings, defaulting to fields
 * of those names. `sourceUrl`, `topic` and `location` also read named fields
 * when the schema has them. Source metadata remains available to consumers even
 * though the article-detail UI does not render it.
 */
export function entryHeader(collection, entry, resolveImage) {
    const { display } = collection;
    const { fields } = entry;
    const scalar = (key) => key ? (fields.scalars[key] ?? '') || undefined : undefined;
    const named = (key) => (fields.scalars[key] ?? '') || undefined;
    const imageRef = display.imageField ? fields.media[display.imageField] : null;
    return {
        // The entry's own title, exactly as entryCard reads it: a collection that
        // names a titleField overrides it, and one that does not falls back to the
        // page title rather than showing a masthead with no heading.
        heading: scalar(display.titleField) ?? entry.title,
        date: formatEntryDate(scalar(display.dateField)),
        topic: named('topic'),
        location: named('location'),
        category: scalar(display.categoryField ?? 'category'),
        source: scalar(display.sourceField ?? 'source'),
        sourceUrl: scalar(display.linkField) ?? named('sourceUrl'),
        summary: scalar(display.summaryField),
        image: imageRef && resolveImage ? resolveImage(imageRef) : null,
    };
}
// ── Ordering and filtering ────────────────────────────────────────────────
/** The value a sort or filter field reads for one entry. */
function valueOf(entry, field) {
    if (field === 'publishedAt')
        return entry.publishedAt ?? '';
    if (field === 'title')
        return entry.title;
    if (field === 'slug')
        return entry.slug;
    if (field === 'url')
        return entry.url;
    return entry.fields.scalars[field] ?? '';
}
/**
 * Order entries by a field.
 *
 * Empty values always sort LAST, whichever direction is asked for. An entry
 * whose date nobody filled in is not "the oldest" — it is unknown, and putting
 * it at the top of a newest-first list would be the most visible possible way
 * to be wrong. Ties break on title so the order is stable rather than
 * whatever the store happened to return.
 *
 * Dates are `YYYY-MM-DD` and timestamps are ISO-8601, so string comparison IS
 * chronological comparison for both — no parsing, and no way for an unparseable
 * value to reorder the list.
 */
export function sortEntries(entries, field, direction) {
    const sign = direction === 'asc' ? 1 : -1;
    return [...entries].sort((a, b) => {
        const av = valueOf(a, field);
        const bv = valueOf(b, field);
        if (av === '' && bv === '')
            return a.title.localeCompare(b.title);
        if (av === '')
            return 1;
        if (bv === '')
            return -1;
        if (av === bv)
            return a.title.localeCompare(b.title);
        return av < bv ? -sign : sign;
    });
}
/** Does one entry satisfy one filter? */
export function entryMatchesFilter(entry, filter) {
    const actual = valueOf(entry, filter.field);
    switch (filter.op) {
        case 'eq':
            return actual === filter.value;
        case 'neq':
            return actual !== filter.value;
        case 'contains':
            return actual.toLowerCase().includes(filter.value.toLowerCase());
        case 'gt':
            return actual !== '' && actual > filter.value;
        case 'lt':
            return actual !== '' && actual < filter.value;
        case 'set':
            return actual !== '';
        case 'unset':
            return actual === '';
        default:
            // An op nobody recognises filters nothing out. A list that quietly went
            // empty would be read as "there is no news", which is a worse lie than
            // showing everything.
            return true;
    }
}
/** Every entry that satisfies every filter. */
export function filterEntries(entries, filters) {
    if (filters.length === 0)
        return entries;
    return entries.filter((entry) => filters.every((filter) => entryMatchesFilter(entry, filter)));
}
const FILTER_OPS = [
    'eq',
    'neq',
    'contains',
    'gt',
    'lt',
    'set',
    'unset',
];
/**
 * Parse one `field:op:value` filter token, or `null` when it is malformed.
 *
 * The value keeps every remaining colon, so `date:gt:2026-01-01` works and so
 * would a URL. Only the first two colons are structural.
 */
export function parseEntryFilter(token) {
    const first = token.indexOf(':');
    if (first <= 0)
        return null;
    const second = token.indexOf(':', first + 1);
    const field = token.slice(0, first);
    const op = (second === -1
        ? token.slice(first + 1)
        : token.slice(first + 1, second));
    const value = second === -1 ? '' : token.slice(second + 1);
    if (!FILTER_OPS.includes(op))
        return null;
    // `set` and `unset` take no value; everything else needs one.
    if (op !== 'set' && op !== 'unset' && second === -1)
        return null;
    return { field, op, value };
}
//# sourceMappingURL=collection.js.map